import "./SpacesPage.css";

import { Button, Callout, Card, Eyebrow, Icons, LoadingState, MessageState, Page, TextInput } from "../../../../ds";
import { IChangeRequestService, changeRequestService } from "../../../changeRequests/services/ChangeRequestService";
import { ISpaceService, spaceService } from "../../services/SpaceService";
import { MouseEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SpaceFigures, useSpaceFigures } from "../../hooks/useSpaceFigures";
import { spaceTone } from "../../utils/spaceFields";
import { lockErrorKind, lockErrorMessage } from "../../../locks/services/LockService";

import { AddSpaceDrawer } from "./AddSpaceDrawer";
import { IdentityUnavailableState } from "../../../locks/components/LockStates";
import { Space } from "../../models/Space";
import { useNavigate } from "react-router-dom";

interface ISpacesPageProps {
    /** Injected in tests; the shared clients otherwise. */
    spaces?: ISpaceService;
    changeRequests?: IChangeRequestService;
}

type ListState =
    | { status: "loading" }
    | { status: "ready"; spaces: Space[] }
    | { status: "identity-unavailable" }
    | { status: "error"; message: string };

const byTitle = (a: Space, b: Space) => a.title.localeCompare(b.title);

function matches(space: Space, query: string): boolean {
    if (!query) return true;
    const q = query.toLowerCase();
    return space.title.toLowerCase().includes(q) || (space.description ?? "").toLowerCase().includes(q);
}

/** The footer of a card: the change requests waiting for approval. Drawn only when there are any (`renderCard`). */
function ProposedFact(props: { count: number }) {
    return (
        <span className="ark-space-fact is-proposed" title="Change requests waiting for approval">
            {props.count} proposed
        </span>
    );
}

/** A plain left click follows the route in the app; modified clicks keep the link's own behaviour (new tab, copy). */
function routerClick(navigate: (to: string) => void, to: string) {
    return (ev: MouseEvent<HTMLElement>) => {
        if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
        ev.preventDefault();
        navigate(to);
    };
}

/**
 * Spaces home (board `Ark-Spaces`): the spaces the caller reads as library `Card`s with letter tiles, highlighted spaces first
 * (large cards), then every other space once, a search box and "New space" (a drawer). A card's only figure is the
 * change requests waiting for approval, and only when there are any.
 * Campaign / expedition grouping is not drawn: Ark.Core has no such fields on a space yet.
 */
export function SpacesPage(props: ISpacesPageProps) {
    const navigate = useNavigate();
    const spacesSvc = props.spaces ?? spaceService();
    const crSvc = props.changeRequests ?? changeRequestService();
    const [list, setList] = useState<ListState>({ status: "loading" });
    const [query, setQuery] = useState("");
    const [isAddOpen, setIsAddOpen] = useState(false);
    const searchRef = useRef<HTMLInputElement>(null);

    // The listing holds only the spaces the caller may read; 503 identity-unavailable means Ark cannot tell which (OrbitalPlan/18 §6).
    const fetchSpaces = useCallback((): Promise<ListState> =>
        spacesSvc.getSpaces().then(
            (spaces) => ({ status: "ready", spaces: [...(spaces ?? [])].sort(byTitle) }),
            (error) => lockErrorKind(error) === "identity-unavailable"
                ? { status: "identity-unavailable" }
                : { status: "error", message: lockErrorMessage(error) }), [spacesSvc]);

    useEffect(() => {
        let alive = true;
        fetchSpaces().then((next) => alive && setList(next));
        return () => {
            alive = false;
        };
    }, [fetchSpaces]);

    /** Try again (the loader first) or a quiet refresh after a create. */
    const loadSpaces = useCallback((quiet?: boolean) => {
        if (!quiet) setList({ status: "loading" });
        fetchSpaces().then(setList);
    }, [fetchSpaces]);

    // "/" focuses the search, as the board's key hint says (not while typing elsewhere)
    useEffect(() => {
        const onKey = (ev: KeyboardEvent) => {
            if (ev.key !== "/" || ev.ctrlKey || ev.metaKey || ev.altKey) return;
            const target = ev.target as HTMLElement | null;
            if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
            ev.preventDefault();
            searchRef.current?.focus();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    const spaces = useMemo(() => (list.status === "ready" ? list.spaces : []), [list]);
    const spaceIds = useMemo(() => spaces.map((s) => s.spaceId), [spaces]);
    const figures = useSpaceFigures(spaceIds, spacesSvc, crSvc);

    const visible = useMemo(() => spaces.filter((s) => matches(s, query.trim())), [spaces, query]);
    const highlighted = useMemo(() => visible.filter((s) => s.isHighlighted), [visible]);
    const others = useMemo(() => visible.filter((s) => !s.isHighlighted), [visible]);

    const openAdd = useCallback(() => setIsAddOpen(true), []);
    const closeAdd = useCallback(() => setIsAddOpen(false), []);
    const handleCreated = useCallback(() => loadSpaces(true), [loadSpaces]);

    const renderCard = (space: Space, size: "md" | "lg") => {
        const to = `/spaces/${space.spaceId}`;
        const proposed = figures[space.spaceId]?.proposed;
        return (
            <Card
                key={space.spaceId}
                size={size}
                title={space.title}
                description={space.description}
                tone={spaceTone(space.spaceId)}
                highlighted={space.isHighlighted}
                href={to}
                onClick={routerClick(navigate, to)}
                footer={proposed ? <ProposedFact count={proposed} /> : undefined}
                headingLevel={3}
            />
        );
    };

    const count = spaces.length;

    let body: ReactNode;
    if (list.status === "loading") {
        body = <LoadingState text="Loading spaces…" />;
    } else if (list.status === "identity-unavailable") {
        body = <IdentityUnavailableState onRetry={() => loadSpaces()} />;
    } else if (list.status === "error") {
        body = (
            <Callout tone="danger" title="The spaces could not be loaded">
                <p>{list.message}</p>
                <Button type="link" size="sm" text="Try again" onClick={() => loadSpaces()} />
            </Callout>
        );
    } else if (count === 0) {
        body = (
            <MessageState
                kind="add"
                title="No spaces yet"
                text="Create the first space: it holds a tree of pages. Who can read and edit it is managed in Identity Web."
                actionLabel="New space"
                actionIcon={<Icons.plus />}
                onAction={openAdd}
            />
        );
    } else if (visible.length === 0) {
        body = (
            <MessageState
                title={`No spaces match “${query.trim()}”`}
                text="Search looks at the titles and descriptions of the spaces you can read."
                secondaryLabel="Clear search"
                onSecondary={() => setQuery("")}
                compact
            />
        );
    } else {
        body = (
            <>
                {highlighted.length > 0 ? (
                    <section className="ark-spaces-section" aria-labelledby="ark-spaces-highlighted">
                        <Eyebrow id="ark-spaces-highlighted" count={`${highlighted.length} ${highlighted.length === 1 ? "space" : "spaces"}`}>
                            Highlighted
                        </Eyebrow>
                        <div className="ark-spaces-grid is-lg">{highlighted.map((s) => renderCard(s, "lg"))}</div>
                    </section>
                ) : null}
                {others.length > 0 ? (
                    <section className="ark-spaces-section" aria-labelledby="ark-spaces-all">
                        <Eyebrow
                            id="ark-spaces-all"
                            count={`${others.length} ${others.length === 1 ? "space" : "spaces"}`}
                        >
                            {highlighted.length > 0 ? "More spaces" : "All spaces"}
                        </Eyebrow>
                        <div className="ark-spaces-grid">{others.map((s) => renderCard(s, "md"))}</div>
                    </section>
                ) : null}
            </>
        );
    }

    return (
        <Page
            className="ark-spaces-page"
            eyebrow="Ark · Spaces"
            title="Spaces"
            tabTitle="Spaces"
            actions={
                <div className="ark-spaces-actions">
                    <TextInput
                        ref={searchRef}
                        value={query}
                        onChange={setQuery}
                        placeholder="Search spaces"
                        aria-label="Search spaces"
                        type="search"
                        icon={<Icons.search />}
                        className="ark-spaces-search"
                    />
                    <Button text="New space" type="primary" icon={<Icons.plus />} onClick={openAdd} />
                </div>
            }
        >
            {body}
            <AddSpaceDrawer open={isAddOpen} onClose={closeAdd} onCreated={handleCreated} service={spacesSvc} />
        </Page>
    );
}
